import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthProvider'
import { supabase } from '@/lib/supabase'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { cacheBookings, getCachedBookings } from '@/db/mobileDbOperations'
import { Badge } from '@/components/ui/badge'
import { useNavigate } from 'react-router-dom'
import { Loader2, BookOpen, Calendar, Clock, MapPin, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatBookingWhen, formatBookingTimeRange } from '@/lib/bookingDates'

const statusConfig = {
  confirmed: { label: 'Confirmed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200' },
  booked: { label: 'Booked', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  quoted: { label: 'Quoted', className: 'bg-amber-50 text-amber-700 border-amber-200' },
}

// 'booked' means a place has been requested; it only becomes firm once the
// provider confirms (booking_confirmed_date). Same split as the main app.
const bookedAwaiting = { label: 'Booked - Awaiting', className: 'bg-amber-50 text-amber-700 border-amber-200' }
const bookedConfirmed = { label: 'Booked - Confirmed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' }

function statusFor(booking) {
  if (booking.status === 'booked') {
    return booking.booking_confirmed_date ? bookedConfirmed : bookedAwaiting
  }
  return statusConfig[booking.status] || statusConfig.pending
}

export default function BookingsPage() {
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const isOnline = useOnlineStatus()
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchBookings() {
      if (!userProfile?.id) return

      setLoading(true)

      if (isOnline) {
        try {
          const { data, error } = await supabase.rpc('app_get_bookings_v3', {
            p_user_id: userProfile.id,
          })

          if (error) throw error

          const result = data || []
          setBookings(result)
          await cacheBookings(result)
        } catch (err) {
          console.error('[Bookings] Error:', err)
        }
      } else {
        try {
          const cached = await getCachedBookings()
          if (cached?.length) setBookings(cached)
        } catch {
          // No cache
        }
      }

      setLoading(false)
    }

    fetchBookings()
  }, [userProfile?.id, isOnline])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/more')} className="p-1 -ml-1 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Bookings</h1>
      </div>

      {bookings.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <BookOpen className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No training bookings found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map(b => {
            const config = statusFor(b)
            // The full delivery window ("24 – 26 Feb 2026 · 3 days"), falling back to
            // the legacy single date on bookings that only ever had one.
            const when = formatBookingWhen(b)
            const times = formatBookingTimeRange(b)
            return (
              <div key={b.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm">{b.course_name || 'Unknown Course'}</p>
                    {b.booked_provider && (
                      <p className="text-xs text-gray-500 mt-0.5">{b.booked_provider}</p>
                    )}
                  </div>
                  <Badge variant="outline" className={cn('text-xs flex-shrink-0', config.className)}>
                    {config.label}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {when && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {when}
                    </p>
                  )}
                  {times && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {times}
                    </p>
                  )}
                  {b.booked_location && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {b.booked_location}
                    </p>
                  )}
                  {b.booked_delivery_method && (
                    <p className="text-xs text-gray-400">{b.booked_delivery_method}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
